import React, { useMemo } from "react";

import _ from "lodash";
import { TagsIcon } from "lucide-react";

import { MultiSelect } from "@/components/fields/multi-select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useTags } from "@/contexts/tags-context";
import { useEventRepository } from "@/pages/events/hooks/use-event-repository";

export function PatchInfoModal({
  id,
  name,
  tags: defaultTags,
}: {
  id: string;
  name: string;
  tags: string[];
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [tags, setTags] = React.useState<string[]>(defaultTags);
  const [tagsToRemove, setTagsToRemove] = React.useState<string[]>([]);

  const { tags: usedTags } = useTags();
  const { patch } = useEventRepository();

  const handleClose = async () => {
    setIsOpen(false);
    setTags([]);
    setTagsToRemove([]);
  };

  const handleSubmit = async () => {
    await patch(id, tags, tagsToRemove);
    handleClose();
  };

  const canSubmit = useMemo(() => {
    const tagsNotEmpty = tags.length > 0;
    const tagsChanged = !_.isEqual(tags, defaultTags);
    const removingTags = tagsToRemove.length > 0;
    return removingTags || (tagsNotEmpty && tagsChanged);
  }, []);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && setIsOpen(false)}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          onClick={() => {
            setIsOpen(true);
          }}
        >
          <TagsIcon size={16} />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Patch Event Info {id}</DialogTitle>
        </DialogHeader>
        <DialogDescription>{name}</DialogDescription>

        <div className="space-y-4 py-4">
          <MultiSelect
            options={usedTags}
            selected={tags ?? []}
            onChange={(v) => {
              const newSelectedItems = new Set(v);
              // 2. Filter the main array
              setTagsToRemove(
                defaultTags.filter((item) => !newSelectedItems.has(item)),
              );
              setTags(v);
            }}
            placeholder="Select or type tags..."
          />
          {tagsToRemove.length > 0 && (
            <div className="flex flex-row gap-2 items-center">
              <Label>Tags to remove:</Label>
              {tagsToRemove.map((t) => (
                <Badge key={t}>{t}</Badge>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={canSubmit}>
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
